import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PieChart, Pie, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { MapPin, ShoppingBag, Clock, AlertCircle, BarChart3, FileText, TrendingUp, Package } from 'lucide-react';
import { DateRangeQuickFilter } from '@/components/ui/date-range-quick-filter';
import { useDateRangeDefaults } from '@/hooks/useDateRangeDefaults';
import { useAuth } from '@/context/AuthContext';
import { reportsService } from '../services/reportsService';
import { useAccessibleLocations } from '../hooks/useAccessibleLocations';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

interface ProductFilters {
  locationId: string;
  dateFrom: string;
  dateTo: string;
  shift: string;
  presentation: string;
  category: string;
}

type ViewTab = 'chart' | 'table';

const categoryColors: Record<string, string> = {
  // Categorías en inglés (como vienen de la base de datos)
  'sandwiches': '#3b82f6',
  'hamburguesas': '#22c55e',
  'bebidas': '#8b5cf6',
  'postres': '#f97316',
  'ensaladas': '#10b981',
  'combos': '#f59e0b',
  'otros': '#64748b',
  // También mantener las versiones en español por compatibilidad
  'Hamburguesas': '#22c55e',
  'Sandwiches': '#3b82f6', 
  'Combos': '#f59e0b',
  'Acompañamientos': '#ef4444',
  'Bebidas': '#8b5cf6',
  'General': '#64748b'
};

const categoryLabels: Record<string, string> = {
  'sandwiches': 'Sandwiches',
  'hamburguesas': 'Hamburguesas',
  'bebidas': 'Bebidas',
  'postres': 'Postres',
  'ensaladas': 'Ensaladas',
  'combos': 'Combos',
  'otros': 'Otros'
};

const getCategoryLabel = (category: string): string => {
  return categoryLabels[category] || category.charAt(0).toUpperCase() + category.slice(1);
};

const presentationColors: Record<string, string> = {
  'individual': '#3b82f6',
  'combo': '#22c55e',
  'familiar': '#8b5cf6',
  'promocional': '#f97316',
  'otros': '#64748b'
};

const presentationLabels: Record<string, string> = {
  'individual': 'Individual',
  'combo': 'Combo',
  'familiar': 'Familiar',
  'promocional': 'Promocional',
  'otros': 'Otros'
};

const getPresentationLabel = (presentation: string): string => {
  return presentationLabels[presentation] || presentation.charAt(0).toUpperCase() + presentation.slice(1);
};



const shifts = [
  { id: 'morning', name: 'Mañana' },
  { id: 'afternoon', name: 'Tarde' },
];

// Las presentaciones se cargan dinámicamente de la base de datos

const categories = [
  { id: 'all', name: 'Todas las categorías' },
  { id: 'sandwiches', name: 'Sandwiches' },
  { id: 'hamburguesas', name: 'Hamburguesas' },
  { id: 'bebidas', name: 'Bebidas' },
  { id: 'postres', name: 'Postres' },
  { id: 'ensaladas', name: 'Ensaladas' },
  { id: 'combos', name: 'Combos' },
  { id: 'otros', name: 'Otros' }
];

interface ProductData {
  productId: string;
  name: string;
  codigo: string;
  category: string;
  quantity: number;
  revenue: number;
  percentage: number;
  averagePrice: number;
  transactions: number;
}

export const TopProducts: React.FC = () => {
  const { user } = useAuth();
  const { locationsWithAll, defaultLocationId, loading: locationsLoading } = useAccessibleLocations();

  // Hook para manejar rangos de fechas por defecto
  const {
    quickRange,
    dateFrom,
    dateTo,
    setQuickRange,
    setManualDates
  } = useDateRangeDefaults('topProducts');

  // Estado para las pestañas internas (gráfico vs tabla)
  const [activeView, setActiveView] = useState<ViewTab>('chart');
  const [activePresentationView, setActivePresentationView] = useState<ViewTab>('chart');

  const [filters, setFilters] = useState<ProductFilters>({
    locationId: 'all',
    dateFrom: '',
    dateTo: '',
    shift: 'all',
    presentation: 'all',
    category: 'all'
  });

  // Auto-seleccionar local cuando se carga
  useEffect(() => {
    if (defaultLocationId) {
      setFilters(prev => ({ ...prev, locationId: defaultLocationId }));
    }
  }, [defaultLocationId]);

  const [products, setProducts] = useState<ProductData[]>([]);
  const [presentations, setPresentations] = useState<any[]>([]);
  const [availablePresentations, setAvailablePresentations] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [presentationSummary, setPresentationSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [presentationLoading, setPresentationLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [presentationError, setPresentationError] = useState<string | null>(null);

  const updateFilter = (key: keyof ProductFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Sincronizar las fechas del hook con los filtros
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      dateFrom,
      dateTo
    }));
  }, [dateFrom, dateTo]);

  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    if (field === 'dateFrom') {
      setManualDates(value, dateTo);
    } else {
      setManualDates(dateFrom, value);
    }
  };


  const loadReportData = async () => {
    if (!filters.locationId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await reportsService.getTopProductsReport(filters.locationId, {
        startDate: filters.dateFrom || undefined,
        endDate: filters.dateTo || undefined,
        shift: filters.shift !== 'all' ? filters.shift : undefined,
        presentation: filters.presentation !== 'all' ? filters.presentation : undefined,
        category: filters.category !== 'all' ? filters.category : undefined,
        limit: 15,
        sortBy: 'quantity'
      });

      if (response.success) {
        const productsWithColor = response.data.products.map((product: any) => ({
          ...product,
          color: categoryColors[product.category] || categoryColors['otros'] || '#64748b'
        }));

        setProducts(productsWithColor);
        setSummary(response.data.summary);

        // Si no hay productos en el rango de fechas seleccionado, mostrar mensaje informativo
        if (productsWithColor.length === 0) {
          setError('No se encontraron productos vendidos en el período seleccionado');
        }
      } else {
        setError(response.error || 'Error al cargar datos');
      }
    } catch (err) {
      setError('Error al conectar con el servidor');
      console.error('Error loading top products report:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPresentationData = async () => {
    if (!filters.locationId) return;

    setPresentationLoading(true);
    setPresentationError(null);

    try {
      // Simular datos de presentaciones más vendidas
      const mockPresentations = [
        {
          presentationId: '1',
          name: 'Individual',
          presentation: 'individual',
          quantity: 1250,
          revenue: 18750.00,
          percentage: 45.2,
          averagePrice: 15.00,
          transactions: 1250,
          color: presentationColors['individual']
        },
        {
          presentationId: '2',
          name: 'Combo',
          presentation: 'combo',
          quantity: 890,
          revenue: 22250.00,
          percentage: 32.1,
          averagePrice: 25.00,
          transactions: 890,
          color: presentationColors['combo']
        },
        {
          presentationId: '3',
          name: 'Familiar',
          presentation: 'familiar',
          quantity: 420,
          revenue: 16800.00,
          percentage: 15.2,
          averagePrice: 40.00,
          transactions: 420,
          color: presentationColors['familiar']
        },
        {
          presentationId: '4',
          name: 'Promocional',
          presentation: 'promocional',
          quantity: 210,
          revenue: 4200.00,
          percentage: 7.5,
          averagePrice: 20.00,
          transactions: 210,
          color: presentationColors['promocional']
        }
      ];

      setPresentations(mockPresentations);
      setPresentationSummary({
        totalQuantity: 2770,
        totalRevenue: 62000.00
      });

    } catch (err) {
      setPresentationError('Error al cargar datos de presentaciones');
      console.error('Error loading presentations report:', err);
    } finally {
      setPresentationLoading(false);
    }
  };



  const loadPresentationsDropdown = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/presentation-types`);
      if (response.ok) {
        const result = await response.json();
        console.log('Presentations API response:', result);

        // El API devuelve {success: true, data: [...], meta: {...}}
        if (result.success && Array.isArray(result.data)) {
          setAvailablePresentations(result.data);
          console.log('Loaded presentations:', result.data.length);
        } else {
          console.error('API response format unexpected:', result);
          setAvailablePresentations([]);
        }
      } else {
        console.error('Failed to fetch presentations:', response.status);
        setAvailablePresentations([]);
      }
    } catch (error) {
      console.error('Error loading presentations:', error);
      setAvailablePresentations([]);
    }
  };

  useEffect(() => {
    loadPresentationsDropdown();
  }, []);



  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Reporte de Productos</h2>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Local</Label>
              <Select value={filters.locationId} onValueChange={(value) => updateFilter('locationId', value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Seleccione un local" />
                </SelectTrigger>
                <SelectContent>
                  {locationsWithAll.map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {location.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Rango</Label>
              <DateRangeQuickFilter
                value={quickRange}
                onChange={setQuickRange}
                reportType="topProducts"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Desde</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleDateChange('dateFrom', e.target.value)}
                disabled={quickRange !== 'custom'}
                className="w-[140px]"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Hasta</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleDateChange('dateTo', e.target.value)}
                disabled={quickRange !== 'custom'}
                className="w-[140px]"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Turno</Label>
              <Select value={filters.shift} onValueChange={(value) => updateFilter('shift', value)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {shifts.map(shift => (
                    <SelectItem key={shift.id} value={shift.id}>{shift.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Presentación</Label>
              <Select value={filters.presentation} onValueChange={(value) => updateFilter('presentation', value)}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {Array.isArray(availablePresentations) && availablePresentations.map(presentation => (
                    <SelectItem key={presentation.id} value={presentation.id.toString()}>
                      {presentation.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Categoría</Label>
              <Select value={filters.category} onValueChange={(value) => updateFilter('category', value)}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(category => (
                    <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={() => {
                loadReportData();
                loadPresentationData();
              }}
              disabled={loading || presentationLoading}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
            >
              {(loading || presentationLoading) ? 'Cargando...' : 'Generar Reporte'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
              <div>
                <p className="font-medium text-red-900">Error al cargar datos</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resumen estadístico */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-green-200 bg-green-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-800">Total Unidades Vendidas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-900">
                {summary.totalQuantity?.toLocaleString('es-PE') || '0'}
              </div>
              <p className="text-xs text-green-700">
                Cantidad total de productos vendidos
              </p>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-blue-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-blue-800">Ingresos Totales</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-900">
                S/ {summary.totalRevenue?.toLocaleString('es-PE', { minimumFractionDigits: 2 }) || '0.00'}
              </div>
              <p className="text-xs text-blue-700">
                Ingresos generados por productos
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Nueva sección: Todos los productos vendidos con pestañas */}
      {products.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Package className="w-5 h-5 mr-2" />
              Todos los Productos Vendidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="units" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="units">Por Unidades</TabsTrigger>
                <TabsTrigger value="sales">Por Ventas</TabsTrigger>
              </TabsList>

              <TabsContent value="units" className="mt-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Producto</TableHead>
                        <TableHead>Categoría</TableHead>
                        <TableHead className="text-right">Unidades Vendidas</TableHead>
                        <TableHead className="text-right">Precio Promedio</TableHead>
                        <TableHead className="text-right">Total Transacciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products
                        .sort((a, b) => b.quantity - a.quantity)
                        .map((product, index) => (
                        <TableRow key={product.productId}>
                          <TableCell className="font-medium">{index + 1}</TableCell>
                          <TableCell className="font-medium">{product.name}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                  style={{ backgroundColor: `${product.color}20`, color: product.color }}>
                              {getCategoryLabel(product.category)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-semibold">{product.quantity.toLocaleString('es-PE')}</TableCell>
                          <TableCell className="text-right">S/ {product.averagePrice?.toLocaleString('es-PE', { minimumFractionDigits: 2 }) || '0.00'}</TableCell>
                          <TableCell className="text-right">{product.transactions?.toLocaleString('es-PE') || '0'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="sales" className="mt-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Producto</TableHead>
                        <TableHead>Categoría</TableHead>
                        <TableHead className="text-right">Total de Ventas</TableHead>
                        <TableHead className="text-right">Unidades Vendidas</TableHead>
                        <TableHead className="text-right">Precio Promedio</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products
                        .sort((a, b) => b.revenue - a.revenue)
                        .map((product, index) => (
                        <TableRow key={product.productId}>
                          <TableCell className="font-medium">{index + 1}</TableCell>
                          <TableCell className="font-medium">{product.name}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                  style={{ backgroundColor: `${product.color}20`, color: product.color }}>
                              {getCategoryLabel(product.category)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-green-600">
                            S/ {product.revenue.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-right">{product.quantity.toLocaleString('es-PE')}</TableCell>
                          <TableCell className="text-right">S/ {product.averagePrice?.toLocaleString('es-PE', { minimumFractionDigits: 2 }) || '0.00'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Pestañas para Vista de Datos */}
      {products.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle>Top 5 Productos Más Vendidos</CardTitle>
              <div className="flex space-x-2">
                <Button
                  variant={activeView === 'chart' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveView('chart')}
                  className={`${activeView === 'chart' ? 'bg-macartur-primary hover:bg-macartur-primary/90' : ''} transition-colors`}
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Gráfico
                </Button>
                <Button
                  variant={activeView === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveView('table')}
                  className={`${activeView === 'table' ? 'bg-macartur-primary hover:bg-macartur-primary/90' : ''} transition-colors`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Detalle
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {activeView === 'chart' ? (
              <>
                <div data-testid="top-products-chart" className="w-full h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={products.slice(0, 5)}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={120}
                        paddingAngle={3}
                        dataKey="quantity"
                        label={({ name, percentage }) => `${name}: ${percentage}%`}
                        labelLine={false}
                      >
                        {products.slice(0, 5).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [
                          `${value} unidades`,
                          'Cantidad Vendida'
                        ]}
                        labelFormatter={(label) => {
                          const product = products.find(p => p.name === label);
                          return product ? (
                            <div>
                              <div className="font-semibold">{label}</div>
                              <div className="text-sm text-gray-600">Categoría: {product.category}</div>
                              <div className="text-sm font-medium text-green-600">
                                Ingresos: S/ {product.revenue.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                              </div>
                            </div>
                          ) : label;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Leyenda de categorías */}
                <div className="mt-4 flex flex-wrap justify-center gap-4">
                  {Object.entries(categoryLabels).map(([category, label]) => (
                    <div key={category} className="flex items-center">
                      <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: categoryColors[category] }}></div>
                      <span className="text-sm">{label}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ranking</TableHead>
                      <TableHead>Producto</TableHead>
                      <TableHead>Categoría</TableHead>
                      <TableHead className="text-right">Cantidad Vendida</TableHead>
                      <TableHead className="text-right">Ingresos</TableHead>
                      <TableHead className="text-right">% del Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product, index) => (
                      <TableRow key={product.productId}>
                        <TableCell>
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                               style={{ backgroundColor: product.color }}>
                            {index + 1}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                style={{ backgroundColor: `${product.color}20`, color: product.color }}>
                            {getCategoryLabel(product.category)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{product.quantity}</TableCell>
                        <TableCell className="text-right font-semibold">S/ {product.revenue.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium text-blue-600">{product.percentage}%</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sección de Presentaciones Más Vendidas */}
      {presentationSummary && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-purple-200 bg-purple-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-purple-800">Total Unidades por Presentación</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-900">
                {presentationSummary.totalQuantity?.toLocaleString('es-PE') || '0'}
              </div>
              <p className="text-xs text-purple-700">
                Cantidad total de presentaciones vendidas
              </p>
            </CardContent>
          </Card>

          <Card className="border-orange-200 bg-orange-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-orange-800">Ingresos por Presentaciones</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-900">
                S/ {presentationSummary.totalRevenue?.toLocaleString('es-PE', { minimumFractionDigits: 2 }) || '0.00'}
              </div>
              <p className="text-xs text-orange-700">
                Ingresos generados por presentaciones
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pestañas para Vista de Presentaciones */}
      {presentations.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle>Top 5 Presentaciones Más Vendidas</CardTitle>
              <div className="flex space-x-2">
                <Button
                  variant={activePresentationView === 'chart' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActivePresentationView('chart')}
                  className={`${activePresentationView === 'chart' ? 'bg-macartur-primary hover:bg-macartur-primary/90' : ''} transition-colors`}
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Gráfico
                </Button>
                <Button
                  variant={activePresentationView === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActivePresentationView('table')}
                  className={`${activePresentationView === 'table' ? 'bg-macartur-primary hover:bg-macartur-primary/90' : ''} transition-colors`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Detalle
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {activePresentationView === 'chart' ? (
              <>
                <div data-testid="top-presentations-chart" className="w-full h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={presentations.slice(0, 5)}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={120}
                        paddingAngle={3}
                        dataKey="quantity"
                        label={({ name, percentage }) => `${name}: ${percentage}%`}
                        labelLine={false}
                      >
                        {presentations.slice(0, 5).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [
                          `${value} unidades`,
                          'Cantidad Vendida'
                        ]}
                        labelFormatter={(label) => {
                          const presentation = presentations.find(p => p.name === label);
                          return presentation ? (
                            <div>
                              <div className="font-semibold">{label}</div>
                              <div className="text-sm text-gray-600">Presentación: {presentation.presentation}</div>
                              <div className="text-sm font-medium text-green-600">
                                Ingresos: S/ {presentation.revenue.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                              </div>
                            </div>
                          ) : label;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Leyenda de presentaciones */}
                <div className="mt-4 flex flex-wrap justify-center gap-4">
                  {Object.entries(presentationLabels).map(([presentation, label]) => (
                    <div key={presentation} className="flex items-center">
                      <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: presentationColors[presentation] }}></div>
                      <span className="text-sm">{label}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ranking</TableHead>
                      <TableHead>Presentación</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Cantidad Vendida</TableHead>
                      <TableHead className="text-right">Ingresos</TableHead>
                      <TableHead className="text-right">% del Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {presentations.map((presentation, index) => (
                      <TableRow key={presentation.presentationId}>
                        <TableCell>
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                               style={{ backgroundColor: presentation.color }}>
                            {index + 1}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{presentation.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" style={{ backgroundColor: `${presentation.color}20`, color: presentation.color }}>
                            {getPresentationLabel(presentation.presentation)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{presentation.quantity?.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-semibold">S/ {presentation.revenue?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium text-blue-600">{presentation.percentage}%</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Error de Presentaciones */}
      {presentationError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
              <p className="text-red-700">{presentationError}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-lg">Cargando datos del reporte...</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && !error && products.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <ShoppingBag className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <div className="text-lg font-medium text-gray-900">No hay datos de productos</div>
              <div className="text-sm text-gray-500">Selecciona filtros y genera el reporte para ver los productos más vendidos</div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};